import React from 'react';
import { Link } from 'react-router-dom';
import { Users, Briefcase, Globe, TrendingUp, Home, ArrowRight, CheckCircle } from 'lucide-react';

const segments = [
  {
    id: 'individuals',
    icon: Users,
    title: 'Individuals & Families',
    subtitle: 'W-2 employees, retirees, students',
    desc: 'Whether you\'re a first-time filer or have a complex return with multiple income sources, we make tax season stress-free.',
    benefits: ['W-2 income filing', 'Retirement income', 'Investment gains/losses', 'Child and dependent credits', 'Education credits', 'Medical expense deductions'],
    image: 'https://images.unsplash.com/photo-1769674109078-da12f5cc7871?w=600&q=80',
    cta: 'File My Individual Return'
  },
  {
    id: 'self-employed',
    icon: Briefcase,
    title: 'Self-Employed & Freelancers',
    subtitle: '1099 contractors, gig workers, consultants',
    desc: 'Self-employment taxes are complex. We maximize your deductions and ensure you\'re paying the right estimated taxes throughout the year.',
    benefits: ['Schedule C preparation', 'Self-employment tax calculation', 'Home office deduction', 'Business mileage tracking', 'Health insurance deduction', 'Quarterly estimated taxes'],
    image: 'https://images.unsplash.com/photo-1636038197596-28e7dce070e2?w=600&q=80',
    cta: 'Maximize My 1099 Return'
  },
  {
    id: 'businesses',
    icon: TrendingUp,
    title: 'Small & Medium Businesses',
    subtitle: 'LLCs, S-Corps, C-Corps, partnerships',
    desc: 'Business taxes require specialized knowledge. From entity selection to year-end planning, we\'re your full-service business tax partner.',
    benefits: ['Business entity returns (1120, 1120-S, 1065)', 'Payroll tax compliance', 'Business deduction optimization', 'Depreciation and amortization', 'Tax strategy and planning', 'Multi-year projections'],
    image: 'https://images.unsplash.com/photo-1758691736975-9f7f643d178e?w=600&q=80',
    cta: 'Business Tax Consultation'
  },
  {
    id: 'multi-state',
    icon: Globe,
    title: 'Multi-State Filers',
    subtitle: 'Remote workers, travelers, dual residents',
    desc: 'Working or living across state lines creates unique tax obligations. We navigate the complexity so you stay compliant in every state.',
    benefits: ['Multi-state income allocation', 'Residency determination', 'Credits for taxes paid to other states', 'Remote work tax implications', 'Travel nursing taxes', 'Domicile planning'],
    image: 'https://images.unsplash.com/photo-1714601344981-75e003bc5d18?w=600&q=80',
    cta: 'Multi-State Filing Help'
  },
  {
    id: 'startups',
    icon: Home,
    title: 'Startups & New Businesses',
    subtitle: 'Early-stage companies, founders',
    desc: 'Getting your tax structure right from day one saves thousands. We set up your startup for long-term tax efficiency.',
    benefits: ['Entity structure optimization', 'Startup cost deductions', 'R&D tax credits', 'Founder equity and compensation', 'Investor documentation', 'Multi-year tax planning'],
    image: 'https://images.unsplash.com/photo-1644810205147-dc105542b92a?w=600&q=80',
    cta: 'Startup Tax Consultation'
  },
];

const WhoWeHelpPage = () => (
  <div style={{ paddingTop: '72px' }}>
    <section className="py-16 px-6" style={{ background: '#0B1F3A' }}>
      <div className="max-w-4xl mx-auto text-center">
        <div className="label-uppercase mb-4" style={{ color: '#D4AF37' }}>Who We Help</div>
        <h1 className="font-heading text-5xl font-extrabold text-white tracking-tight mb-6">Tax Help for Everyone</h1>
        <p className="text-blue-200 text-xl max-w-2xl mx-auto">
          From simple W-2 returns to complex multi-state business filings — we have the expertise to handle your unique tax situation.
        </p>
      </div>
    </section>

    {segments.map((seg, i) => {
      const Icon = seg.icon;
      return (
        <section key={seg.id} id={seg.id} className={`py-20 px-6 ${i % 2 === 0 ? 'bg-white' : 'bg-[#F8FAFC]'}`}>
          <div className="max-w-7xl mx-auto">
            <div className={`grid lg:grid-cols-2 gap-16 items-center ${i % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}>
              <div className={i % 2 !== 0 ? 'lg:order-2' : ''}>
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: '#0B1F3A' }}>
                    <Icon size={24} className="text-[#D4AF37]" />
                  </div>
                  <div>
                    <div className="font-heading font-bold text-[#0B1F3A] text-xl">{seg.title}</div>
                    <div className="text-[#475569] text-sm">{seg.subtitle}</div>
                  </div>
                </div>
                <p className="text-[#475569] text-lg leading-relaxed mb-8">{seg.desc}</p>
                <div className="grid grid-cols-2 gap-3 mb-8">
                  {seg.benefits.map((b) => (
                    <div key={b} className="flex items-center gap-2">
                      <CheckCircle size={16} style={{ color: '#10B981', flexShrink: 0 }} />
                      <span className="text-sm text-[#475569]">{b}</span>
                    </div>
                  ))}
                </div>
                <Link to="/book-appointment">
                  <button className="btn-primary px-8 py-3.5">
                    {seg.cta} <ArrowRight size={18} />
                  </button>
                </Link>
              </div>
              <div className={i % 2 !== 0 ? 'lg:order-1' : ''}>
                <img src={seg.image} alt={seg.title} className="w-full h-96 object-cover rounded-2xl shadow-floating" />
              </div>
            </div>
          </div>
        </section>
      );
    })}

    <section className="py-20 px-6" style={{ background: '#0B1F3A' }}>
      <div className="max-w-4xl mx-auto text-center">
        <h2 className="font-heading text-4xl font-bold text-white tracking-tight mb-6">Not Sure Where You Fit?</h2>
        <p className="text-blue-200 text-lg mb-10">Book a free consultation and we'll assess your situation and recommend the best approach.</p>
        <div className="flex flex-wrap justify-center gap-4">
          <Link to="/book-appointment">
            <button className="inline-flex items-center gap-2 px-10 py-4 rounded-full font-bold text-[#0B1F3A]" style={{ background: '#D4AF37' }}>
              Book Free Consultation <ArrowRight size={18} />
            </button>
          </Link>
          <Link to="/contact">
            <button className="inline-flex items-center gap-2 px-10 py-4 rounded-full font-bold text-white border-2 border-white/30 hover:bg-white/10 transition-all duration-200">
              Contact Us
            </button>
          </Link>
        </div>
      </div>
    </section>
  </div>
);

export default WhoWeHelpPage;
